import notify from "./notifications/notify"

const applicationJson = "application/json"

const csrfRegex = new RegExp("csrftoken=([^;]+)")
function getCsrfToken() {
  return csrfRegex.exec(document.cookie)?.[1] ?? ""
}

export const extraHeaders: Record<string, string> = {}

function headers() {
  return new Headers({
    ...extraHeaders,
    "X-CSRFToken": getCsrfToken(),
    Accept: applicationJson,
    "content-type": applicationJson,
  })
}

const credentials =
  import.meta.env.VITE_APP_API_URL === "/" ? "same-origin" : "include"

interface Options {
  aborter?: AbortSignal
}

export function get<T>(url: string, options: Options = {}): Promise<T> {
  return callApi<T>(
    url,
    {
      credentials,
      headers: new Headers(extraHeaders),
    },
    options,
  )
}

function hasFile(data: any) {
  for (const key in data) {
    if (data[key] instanceof FileList || data[key] instanceof Blob) {
      return true
    }
  }
  return false
}

function postPutPatch<T>(
  method: "POST" | "PUT" | "PATCH",
  url: string,
  data: any,
  options: Options = {},
) {
  if (hasFile(data)) {
    return postPutPatchFiles<T>(method, url, data, options)
  }
  return callApi<T>(
    url,
    {
      credentials,
      method: method,
      headers: headers(),
      body: JSON.stringify(data),
    },
    options,
  )
}

export function post<T>(
  url: string,
  data: any,
  options: Options = {},
): Promise<T> {
  return postPutPatch<T>("POST", url, data, options)
}

export function put<T>(
  url: string,
  data: any,
  options: Options = {},
): Promise<T> {
  return postPutPatch<T>("PUT", url, data, options)
}

export function patch<T>(
  url: string,
  data: any,
  options: Options = {},
): Promise<T> {
  return postPutPatch<T>("PATCH", url, data, options)
}

export function del<T>(
  url: string,
  data?: any,
  options: Options = {},
): Promise<T> {
  return callApi<T>(
    url,
    {
      credentials,
      method: "DELETE",
      headers: headers(),
      body: data ? JSON.stringify(data) : undefined,
    },
    options,
  )
}

function postPutPatchFiles<T>(
  method: "POST" | "PUT" | "PATCH",
  url: string,
  data: any,
  options: Options = {},
): Promise<T> {
  let formData = new FormData()
  for (let key in data) {
    if (data[key] instanceof FileList) {
      for (let file of data[key]) {
        formData.append(key, file)
      }
    } else if (data[key] instanceof Blob) {
      if (data[key].type.includes("audio/mpeg")) {
        formData.append(key, data[key], "audio.mp3")
      } else if (data[key].type.includes("audio/webm")) {
        formData.append(key, data[key], "audio.webm")
      } else if (data[key].type.includes("audio/ogg")) {
        formData.append(key, data[key], "audio.ogg")
      } else {
        formData.append(key, data[key])
      }
    } else {
      formData.append(key, data[key])
    }
  }
  return callApi<T>(
    url,
    {
      credentials,
      method: method,
      headers: new Headers({
        ...extraHeaders,
        "X-CSRFToken": getCsrfToken(),
        Accept: applicationJson,
      }),
      body: formData,
    },
    options,
  )
}

export class ApiError extends Error {
  response: Response
  constructor(message: string, response: Response, options?: ErrorOptions) {
    super(message, options)
    this.response = response
  }
}

async function callApi<T>(
  url: string,
  request: RequestInit,
  options: Options,
): Promise<T> {
  if (!url.startsWith("http")) {
    url = import.meta.env.VITE_APP_API_URL + url
  }
  if (options.aborter) {
    request.signal = options.aborter
  }
  const response = await fetch(url, request)
  if (!response.ok) {
    notify({
      text: "An API call failed",
      style: "error",
    })
    throw new ApiError("An API call failed", response)
  }
  if (response.status === 204) {
    // @ts-ignore if there is no data return null, assume T would be set to null
    return {}
  }
  // Trust the caller that this will end up as T
  return response.json()
}
